import type { AgentCard as AgentCardDto } from '@ralph/contracts'
import clsx from 'clsx'
import { Layers, Plus, Search, SlidersHorizontal, Tag, UserRound } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { detailsHref } from '@/components/marketplace/agent-sheet.ts'
import { AgentCard } from '@/components/marketplace/consumer/AgentCard.tsx'
import { Button, EmptyState } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useGallery } from '@/hooks/use-marketplace.ts'
import { useI18n, useT } from '@/i18n/index.ts'

/**
 * The marketplace gallery — `/agents` for every authenticated user (AGM-001).
 *
 * Search and category filtering are server-side round trips rather than a client filter over
 * a cached list. That is not laziness: AGM-006 defines a deterministic ranking (exact name,
 * then Official, then featured rank, then text similarity) that only the SQL knows, and a
 * client-side `includes()` would quietly re-rank the results into something else. The pills
 * below therefore set query state and wait, exactly as the old `<select>` did.
 *
 * **The only count on the page is the one the API can actually answer.** The reference design
 * puts a tally on every filter pill; this gallery has no per-category tally to render, because
 * `GET /api/marketplace/agents` returns a *filtered* page rather than a breakdown, and three
 * of its five rails are truncated server-side at twelve. What it does return whole is `all`
 * (every published listing this viewer may see) and `mine` (their own, drafts included), so a
 * de-duplicated union of those two is an exact count of what this page can show under the
 * current query — and it is rendered on the pill that produced it and nowhere else.
 */
export const AgentsGalleryPage = () => {
  // In the body, never at module scope: the whole page would otherwise keep the language it
  // was first imported in, with no type error and no failing test (PRD-27 F-3, LOC-012).
  const t = useT().marketplace.gallery.gallery
  const session = useSession()
  const [typed, setTyped] = useState('')
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')

  // Debounced so a five-letter query is one request rather than five; short enough that the
  // list still feels like it is following the typing.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed.trim()), 250)
    return () => clearTimeout(timer)
  }, [typed])

  const gallery = useGallery({ search, category })
  const isAdmin = (session.data?.user?.roles ?? []).includes('admin')

  const data = gallery.data
  const filtering = search !== '' || category !== ''

  /*
   * How many distinct agents this response holds.
   *
   * The union is over `all` and `mine` alone, and that is exhaustive rather than a shortcut:
   * the other three rails are built server-side by filtering `all`, so an agent in Featured,
   * Shared with your groups or Recently used is in `all` too. It doubles as the empty check — zero here
   * means every rail is empty, which is the only reading of "nothing to show" that survives
   * the truncation on those three.
   */
  const visibleCount = useMemo(() => {
    if (!data) return 0
    const ids = new Set<string>()
    for (const card of [...data.all, ...data.mine]) ids.add(card.agentId)
    return ids.size
  }, [data])

  const clearFilters = () => {
    setTyped('')
    setSearch('')
    setCategory('')
  }

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-8 sm:px-6 lg:px-10">
      <div className="mx-auto w-full max-w-[1032px]">
        <div className="flex flex-col gap-4 pt-4 pb-[18px] sm:flex-row sm:items-end">
          <div className="min-w-0">
            <h1 className="text-[30px] sm:text-[38px]">{t.title}</h1>
            <p className="mt-[7px] max-w-[430px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{t.subtitle}</p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-[7px] sm:ml-auto">
            {/*
              The maker's own shelf, which was an orphan route until this link existed: nothing
              in the product reached `/agents/mine` except the builder's back pill, so the only
              way to your own agents was to start creating another one. Ungated, because
              creating an agent is (`/agents/new`, beside it).

              It is also where a co-editor invitation is accepted, and that is the half of the
              hole that mattered — an invitation nobody can navigate to is an invitation nobody
              answers.
            */}
            <Link to="/agents/mine">
              <Button variant="mono" icon={<UserRound size={11} strokeWidth={2.2} aria-hidden />}>
                {t.mine}
              </Button>
            </Link>
            {isAdmin && (
              <Link to="/agents/studio">
                <Button variant="mono" icon={<SlidersHorizontal size={11} strokeWidth={2.2} aria-hidden />}>
                  {t.studio}
                </Button>
              </Link>
            )}
            <Link to="/agents/new">
              <Button variant="primary" icon={<Plus size={13} strokeWidth={2.4} aria-hidden />}>
                {t.newAgent}
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-[7px] pb-4">
          {/*
            A group with a name, rather than six loose buttons: the pills replaced a labelled
            `<select>`, and dropping the label with it would leave a screen-reader user with a
            row of unrelated toggles and no idea what they filter.
          */}
          {/* biome-ignore lint/a11y/useSemanticElements: a fieldset defaults to
              `min-inline-size: min-content`, so a row of pills inside one refuses to wrap and
              scrolls the page sideways on a phone. These are toggles over a query parameter
              rather than form fields, and the role plus the label names them without that. */}
          <div role="group" aria-label={t.categoryLabel} className="flex min-w-0 flex-wrap items-center gap-[7px]">
            <FilterPill
              label={t.categoryAll}
              icon={<Layers size={11} strokeWidth={2.2} aria-hidden />}
              active={category === ''}
              count={category === '' ? visibleCount : undefined}
              onClick={() => setCategory('')}
            />
            {(data?.categories ?? []).map((name) => (
              <FilterPill
                key={name}
                label={name}
                icon={<Tag size={11} strokeWidth={2.2} aria-hidden />}
                active={category === name}
                count={category === name ? visibleCount : undefined}
                onClick={() => setCategory(name)}
              />
            ))}
          </div>

          {/*
            Hand-rolled rather than the `Input` primitive: that control is a 34px field with a
            13px radius, and one square box in a row of pills reads as a control that belongs
            to a different screen. Everything else about it — the tokens, the focus ring from
            the base layer, the accessible name — is the primitive's.
          */}
          <div className="relative ml-auto min-w-[190px] flex-1 sm:max-w-[248px]">
            <Search
              size={12}
              strokeWidth={2.2}
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-subtle"
            />
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchLabel}
              className="h-[30px] w-full rounded-full border border-rule bg-card pr-3 pl-8 text-[12px] text-fg placeholder:text-fg-subtle focus:border-primary focus:outline-none"
            />
          </div>

          {filtering && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t.clearFilters}
            </Button>
          )}
        </div>

        <div className="space-y-7 pb-2">
          {gallery.isError && <EmptyState title={t.failed.title} body={t.failed.body} />}

          {!gallery.isError && gallery.isLoading && !data && <p className="text-[13px] text-fg-subtle">{t.loading}</p>}

          {data && (
            <>
              <Rail title={t.sections.mine} cards={data.mine} mine />
              <Rail title={t.sections.featured} cards={data.featured} />
              <Rail title={t.sections.sharedWithYourGroups} cards={data.sharedWithYourGroups} />
              <Rail title={t.sections.recent} cards={data.recent} />
              <Rail title={t.sections.all} cards={data.all} />

              {visibleCount === 0 &&
                (filtering ? (
                  <EmptyState
                    title={t.emptySearch.title}
                    body={t.emptySearch.body}
                    action={
                      <Button size="sm" onClick={clearFilters}>
                        {t.clearFilters}
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    title={t.empty.title}
                    body={t.empty.body}
                    action={
                      <Link to="/agents/new">
                        <Button size="sm" icon={<Plus size={13} strokeWidth={2.4} aria-hidden />}>
                          {t.newAgent}
                        </Button>
                      </Link>
                    }
                  />
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * One category filter.
 *
 * `aria-pressed` rather than a radio group: these are toggles over a query parameter, and the
 * negative assertion the conversation screen carries against `role="radio"` is a reminder
 * that a radio in this product means a choice being *made*, not a view being narrowed.
 */
const FilterPill = ({
  label,
  icon,
  active,
  count,
  onClick
}: {
  label: string
  icon: ReactNode
  active: boolean
  count?: number
  onClick: () => void
}) => {
  // The tally is a number the reader reads, so it takes the locale's grouping: "1,024" in
  // English, "1.024" in Portuguese. Read in the body — a module-scope capture would freeze it.
  const { formatCount } = useI18n()

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        'inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border px-3 text-[12px] tracking-[-0.01em]',
        'transition-[color,border-color,background-color,transform,box-shadow] duration-200 ease-out',
        'hover:-translate-y-px hover:border-rule-strong hover:text-fg',
        active ? 'border-rule-strong bg-card-solid font-semibold text-fg' : 'border-rule bg-transparent text-fg-muted'
      )}
    >
      {icon}
      {/* Category names come from an administrator's list, not from a fixed vocabulary, so the
          label is capped rather than trusted — one long category must not push the row wide
          enough to scroll the page sideways. */}
      <span className="max-w-[18ch] truncate">{label}</span>
      {count !== undefined && <span className="readout text-[9px] text-fg-subtle">{formatCount(count)}</span>}
    </button>
  )
}

/**
 * One titled row of cards.
 *
 * Renders nothing when empty rather than showing a titled void — a gallery with four empty
 * headings reads as broken, and the page-level empty state covers the genuinely-empty case
 * with copy that says what to do next.
 *
 * The heading carries no count on purpose: Featured, Shared with your groups and Recently used are cut
 * off at twelve by the server, so a number beside them would be the length of a slice being
 * read as the size of a set.
 *
 * The "Yours" rail is the one place a draft can appear, and a draft has no listing page to
 * link to, so it routes to the builder instead.
 */
const Rail = ({ title, cards, mine = false }: { title: string; cards: AgentCardDto[]; mine?: boolean }) => {
  if (cards.length === 0) return null

  return (
    <section>
      <div className="mb-3 flex items-center gap-3">
        <h2 className="eyebrow shrink-0">{title}</h2>
        <span className="h-px flex-1 bg-rule" aria-hidden />
      </div>

      {/*
        The stagger lives on the `<li>` rather than on the card, and that is the whole reason
        there is a wrapper at all: `.rise` finishes on `transform: none`, and an animation with
        `fill-mode: both` keeps applying its final frame above every normal declaration — put
        it on the card and the hover lift is silently dead. The delay is capped so a long rail
        does not spend two seconds arriving.
      */}
      <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card, index) => {
          // A draft has no listing page, so its card already goes to the sheet — and a second
          // control beside it would be a duplicate link to the same route and a duplicate tab
          // stop. The accelerator exists for the cards that go somewhere else.
          const opensSheet = mine && card.state === 'draft'

          return (
            <li
              key={card.agentId}
              className="group relative rise"
              style={{ animationDelay: `${Math.min(index, 9) * 40}ms` }}
            >
              <AgentCard card={card} to={opensSheet ? `/agents/${card.agentId}/edit` : `/agents/${card.agentId}`} />
              {!opensSheet && <DetailsAction agentId={card.agentId} displayName={card.displayName} />}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * The gallery's shortcut into an agent's detail sheet (DTL-002).
 *
 * **A sibling of the card's link, never a child of it.** The card is one `<a>` whose
 * accessible name is how the browser suite finds it, and whose own docblock says so; a second
 * link nested inside would make that name ambiguous and put a control inside a control, which
 * no assistive technology has a good answer for. It is positioned over the card's one empty
 * corner instead, and reached by keyboard as the next stop after the card.
 *
 * **Neutral, and it names its agent** (DTL-003). "Details for Expense Buddy" rather than
 * "Edit": twelve cards means twelve of these, and a control called "Details" twelve times is
 * twelve identical announcements. The label does not depend on what the reader may do, because
 * deciding that per card would put a permission question on the one surface whose entire job
 * is scanning — the listing page has the room and the role, and that is where the wording
 * commits.
 *
 * It fades in on hover and on focus, and is fully opaque wherever hover does not exist, so a
 * phone shows it always rather than hiding it behind a gesture that device cannot make.
 */
const DetailsAction = ({ agentId, displayName }: { agentId: string; displayName: string }) => {
  // Its own read, because it is its own component: twelve of these render per rail and each
  // one names its agent in the reader's language.
  const label = useT().marketplace.gallery.details.cardAction(displayName)

  return (
    <Link
      to={detailsHref(agentId)}
      aria-label={label}
      title={label}
      className={clsx(
        'absolute top-[9px] right-[9px] z-10 flex h-[26px] w-[26px] items-center justify-center rounded-full',
        'border border-rule bg-card-solid text-fg-subtle shadow-glass',
        'transition-[opacity,color,border-color,transform] duration-200 ease-out',
        'hover:-translate-y-px hover:border-rule-strong hover:text-fg',
        'focus-visible:opacity-100 focus-visible:[box-shadow:var(--ring)]',
        'opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100'
      )}
    >
      <SlidersHorizontal size={12} strokeWidth={2.2} aria-hidden />
    </Link>
  )
}
